import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Filter, Plus, Trash2, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiErrorText } from '../../utils/apiErrorText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', keywords: '', counterparty_iban: '', direction: 'out', max_amount: '', record_cost: false, project_id: '', category: '', subcategory: '', active: true };

const RuleForm = ({ value, onChange, projects, categories }) => {
  const { t } = useTranslation();
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="bank-rule-form">
      <div className="sm:col-span-2"><Label className="text-xs">{t('bankRules.name')}</Label><Input value={value.name} onChange={(e) => set('name')(e.target.value)} placeholder={t('bankRules.namePh')} data-testid="bank-rule-name" /></div>
      <div className="sm:col-span-2"><Label className="text-xs">{t('bankRules.keywords')}</Label><Input value={value.keywords} onChange={(e) => set('keywords')(e.target.value)} placeholder={t('bankRules.keywordsPh')} data-testid="bank-rule-keywords" /></div>
      <div><Label className="text-xs">{t('bankRules.iban')}</Label><Input value={value.counterparty_iban} onChange={(e) => set('counterparty_iban')(e.target.value)} data-testid="bank-rule-iban" /></div>
      <div><Label className="text-xs">{t('bankRules.maxAmount')}</Label><Input type="number" min="0" step="0.01" value={value.max_amount} onChange={(e) => set('max_amount')(e.target.value)} data-testid="bank-rule-max" /></div>
      <div><Label className="text-xs">{t('bankRules.direction')}</Label>
        <Select value={value.direction} onValueChange={set('direction')}>
          <SelectTrigger className="h-9" data-testid="bank-rule-direction"><SelectValue /></SelectTrigger>
          <SelectContent>{['out', 'in', 'any'].map((d) => <SelectItem key={d} value={d}>{t(`bankRules.dir.${d}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2 self-end pb-2 text-sm"><Checkbox checked={value.active} onCheckedChange={(v) => set('active')(!!v)} data-testid="bank-rule-active" />{t('bankRules.active')}</label>
      <label className="sm:col-span-2 flex items-center gap-2 text-sm"><Checkbox checked={value.record_cost} onCheckedChange={(v) => set('record_cost')(!!v)} data-testid="bank-rule-record-cost" />{t('bankRules.recordCost')}</label>
      {value.record_cost && <>
        <div><Label className="text-xs">{t('bankRules.project')}</Label>
          <Select value={value.project_id} onValueChange={set('project_id')}>
            <SelectTrigger className="h-9" data-testid="bank-rule-project"><SelectValue placeholder={t('bankRules.pickProject')} /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{[p.project_number, p.name].filter(Boolean).join(' · ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{t('bankRules.category')}</Label>
          <Select value={value.category} onValueChange={set('category')}>
            <SelectTrigger className="h-9" data-testid="bank-rule-category"><SelectValue placeholder={t('bankRules.pickCategory')} /></SelectTrigger>
            <SelectContent>{Object.entries(categories || {}).map(([k, c]) => <SelectItem key={k} value={k}>{c?.display_name || k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2"><Label className="text-xs">{t('bankRules.subcategory')}</Label><Input value={value.subcategory} onChange={(e) => set('subcategory')(e.target.value)} placeholder={t('bankRules.subcategoryPh')} data-testid="bank-rule-subcategory" /></div>
      </>}
    </div>
  );
};

const toBody = (v) => ({ ...v, keywords: v.keywords.split(',').map((k) => k.trim()).filter(Boolean), max_amount: v.max_amount ? Number(v.max_amount) : null,
  counterparty_iban: v.counterparty_iban || null, project_id: v.record_cost ? v.project_id || null : null, category: v.record_cost ? v.category || null : null });

/** Bank fee rules: lines matching a rule are ignored on import and can be booked as a cost. */
export const BankRulesDialog = ({ categories, onApplied }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => axios.get(`${API}/bank/rules`).then((r) => setRules(r.data)).catch(() => {});
  useEffect(() => {
    if (!open) return;
    load();
    axios.get(`${API}/projects`).then((r) => setProjects(Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => {});
  }, [open]);
  const save = async () => {
    try {
      if (edit.id) await axios.put(`${API}/bank/rules/${edit.id}`, toBody(edit));
      else await axios.post(`${API}/bank/rules`, toBody(edit));
      toast.success(t('bankRules.saved')); setEdit(null); load();
    } catch (e) { toast.error(apiErrorText(e, t, 'bank.failed')); }
  };
  const remove = async (r) => {
    try { await axios.delete(`${API}/bank/rules/${r.id}`); load(); } catch (e) { toast.error(apiErrorText(e, t, 'bank.failed')); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen(true)} data-testid="bank-rules-open"><Filter className="h-4 w-4 mr-1" />{t('bankRules.button')}</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEdit(null); onApplied?.(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bank-rules-dialog">
          <DialogHeader><DialogTitle>{t('bankRules.title')}</DialogTitle><DialogDescription>{t('bankRules.hint')}</DialogDescription></DialogHeader>
          {edit ? <RuleForm value={edit} onChange={setEdit} projects={projects} categories={categories} /> : (
            <div className="space-y-2">
              {!rules.length && <p className="text-sm text-slate-500" data-testid="bank-rules-empty">{t('bankRules.empty')}</p>}
              {rules.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-2 rounded border p-2 text-sm" data-testid={`bank-rule-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{r.name} {!r.active && <Badge variant="outline" className="ml-1 text-[10px]">{t('bankRules.off')}</Badge>}</div>
                    <div className="truncate text-xs text-slate-500">{[(r.keywords || []).join(', '), r.counterparty_iban, t(`bankRules.dir.${r.direction}`), r.record_cost ? t('bankRules.booksCost') : t('bankRules.ignoreOnly')].filter(Boolean).join(' · ')} · {t('bankRules.hits', { count: r.hits || 0 })}</div>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit({ ...EMPTY, ...r, keywords: (r.keywords || []).join(', '), max_amount: r.max_amount ?? '', counterparty_iban: r.counterparty_iban || '', project_id: r.project_id || '', category: r.category || '', subcategory: r.subcategory || '' })} data-testid={`bank-rule-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(r)} data-testid={`bank-rule-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="gap-2">
            {edit ? <>
              <Button variant="outline" onClick={() => setEdit(null)} data-testid="bank-rule-cancel">{t('common.cancel')}</Button>
              <Button onClick={save} data-testid="bank-rule-save">{t('common.save')}</Button>
            </> : <Button onClick={() => setEdit({ ...EMPTY })} data-testid="bank-rule-add"><Plus className="h-4 w-4 mr-1" />{t('bankRules.add')}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BankRulesDialog;
